<template>
  <van-overlay :show="!activated" z-index="99999" :lock-scroll="true" class="activation-overlay">
    <div class="activation-container" @click.stop>
      <div class="activation-glow"></div>

      <div class="activation-logo">
        <div class="logo-icon">💰</div>
        <h2 class="logo-title">18 工具箱</h2>
        <p class="logo-sub">专业版 · 激活验证</p>
      </div>

      <div class="activation-form">
        <div class="form-label">请输入 8 位激活码</div>
        <van-field
            v-model="code"
            center
            clearable
            maxlength="8"
            placeholder="XXXXXXXX"
            class="activation-input"
            :formatter="val => val.toUpperCase()"
            @keyup.enter="handleActivate"
        >
          <template #button>
            <van-button
                size="small"
                type="primary"
                :loading="verifying"
                :disabled="code.length < 8"
                loading-text="验证中"
                @click="handleActivate"
            >
              立即激活
            </van-button>
          </template>
        </van-field>

        <div v-if="errorMsg" class="activation-error">
          <van-icon name="warning-o"/> {{ errorMsg }}
        </div>

        <div v-if="successMsg" class="activation-success">
          <van-icon name="passed"/> {{ successMsg }}
        </div>
      </div>

      <div class="activation-footer">
        <p>激活码到期后需重新验证</p>
        <p>如需获取激活码，请通过中转qq群“930076389”联系管理员</p>
        <p style="margin-top:8px;opacity:0.4">v{{ getAppVersion() }}</p>
      </div>
    </div>
  </van-overlay>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { activate, isSessionValid, getSavedCode, getSessionToken, appCheck, clearSession, isOfflineGraceExpired, getAppVersion, isKmsActivationUiForced, clearKmsActivationUIFlag } from "@/utils/appSecurity";
import { isIntegrityCompromised } from "@/utils/integrityGuard";
const _k = Symbol();
const _state = { [_k]: false };
const activated = ref(false);
let _verifyInterval = null;
watch(activated, (val) => {
    if (val && !_state[_k]) {
        activated.value = false;
    }
});
const code = ref("");
const verifying = ref(false);
const errorMsg = ref("");
const successMsg = ref("");
const _setActivated = (val) => {
    _state[_k] = val;
    activated.value = val;
};
const checkActivation = async () => {
    if (isIntegrityCompromised()) {
        _setActivated(false);
        return;
    }
    try {
        const data = await appCheck();
        if (data) {
            if (data.forceUpdate || data.maintenance) {
                _setActivated(true);
                return;
            }
            if (data.sessionValid && data.activated) {
                clearKmsActivationUIFlag();
                _setActivated(true);
                return;
            }
            if (getSessionToken())
                clearSession();
        }
        else {
            if (!isKmsActivationUiForced() && isSessionValid() && !isOfflineGraceExpired()) {
                _setActivated(true);
                return;
            }
            if (isSessionValid() && isOfflineGraceExpired())
                clearSession();
        }
    }
    catch (e) {
        if (!isKmsActivationUiForced() && isSessionValid() && !isOfflineGraceExpired()) {
            _setActivated(true);
            return;
        }
        if (getSessionToken())
            clearSession();
    }
    _setActivated(false);
    code.value = getSavedCode();
};
const handleActivate = async () => {
    if (code.value.length < 8)
        return;
    if (verifying.value)
        return;
    errorMsg.value = "";
    successMsg.value = "";
    verifying.value = true;
    try {
        const data = await activate(code.value);
        if (data.success) {
            successMsg.value = "\u{1F389} \u6FC0\u6D3B\u6210\u529F\uFF01\u6B63\u5728\u8FDB\u5165...";
            setTimeout(() => {
                clearKmsActivationUIFlag();
                _setActivated(true);
            }, 800);
        }
        else {
            errorMsg.value = data.message || "\u6FC0\u6D3B\u7801\u65E0\u6548\u6216\u5DF2\u8FC7\u671F";
        }
    }
    catch (e) {
        const msg = e?.message || "";
        if (msg.includes("KMS_HTTP") || msg === "Failed to fetch" || msg.includes("NetworkError") || msg.includes("network"))
            errorMsg.value = "\u670D\u52A1\u7AEF\u7EF4\u62A4\u4E2D\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5";
        else
            errorMsg.value = msg || "\u6FC0\u6D3B\u5931\u8D25\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5";
    }
    finally {
        verifying.value = false;
    }
};
const onKmsReauthRequired = () => {
    _setActivated(false);
    code.value = getSavedCode();
};
onMounted(() => {
    checkActivation();
    window.addEventListener("lst-kms-reauth-required", onKmsReauthRequired);
    _verifyInterval = setInterval(() => {
        if (isIntegrityCompromised() && activated.value) {
            clearSession();
            _setActivated(false);
        }
    }, 45000 + Math.random() * 15000);
});
onUnmounted(() => {
    window.removeEventListener("lst-kms-reauth-required", onKmsReauthRequired);
    if (_verifyInterval) {
        clearInterval(_verifyInterval);
        _verifyInterval = null;
    }
});
</script>

<style scoped>
.activation-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(10px, env(safe-area-inset-top, 0px)) 12px max(14px, env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: rgba(0, 0, 0, 0.85) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.activation-container {
  width: 88%;
  max-width: 380px;
  max-height: min(92dvh, 680px);
  margin: auto;
  background: linear-gradient(145deg, #1a1d2e 0%, #0d1117 100%);
  border-radius: 20px;
  padding: 40px 24px 30px;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.06),
      0 20px 60px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.activation-glow {
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 120px;
  background: radial-gradient(ellipse, rgba(64, 158, 255, 0.25) 0%, transparent 70%);
  pointer-events: none;
}

.activation-logo {
  text-align: center;
  margin-bottom: 32px;
}

.logo-icon {
  font-size: 48px;
  margin-bottom: 12px;
  animation: logo-float 3s ease-in-out infinite;
}

@keyframes logo-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

.logo-title {
  color: #e8eaed;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px;
  letter-spacing: 1px;
}

.logo-sub {
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  margin: 0;
  letter-spacing: 2px;
}

.activation-form {
  margin-bottom: 24px;
}

.form-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  margin-bottom: 10px;
  padding-left: 4px;
}

.activation-input {
  border-radius: 12px !important;
  overflow: hidden;
}

.activation-input :deep(.van-field__control) {
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 3px;
  font-family: 'Courier New', monospace;
}

.activation-error {
  color: #f56c6c;
  font-size: 13px;
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(245, 108, 108, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.activation-success {
  color: #67c23a;
  font-size: 14px;
  font-weight: bold;
  margin-top: 12px;
  padding: 10px 12px;
  background: rgba(103, 194, 58, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  animation: success-in 0.3s ease;
}

@keyframes success-in {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

.activation-footer {
  text-align: center;
}

.activation-footer p {
  color: rgba(255, 255, 255, 0.2);
  font-size: 11px;
  margin: 4px 0;
  line-height: 1.6;
}
</style>